import React, { Suspense, lazy, useEffect } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import WarmGradientOrbs from "./components/WarmGradientOrbs";
import { Toaster } from "./components/ui/toaster";

// Homepage is eagerly imported — it's the most common landing page
// and users benefit from not waiting on a second chunk for it.
import HomePage from "./pages/HomePage";

// All other routes lazy-load per route, each as its own chunk.
const TechnicalAdvisoryPage = lazy(() => import("./pages/services/TechnicalAdvisoryPage"));
const SourcingPage = lazy(() => import("./pages/services/SourcingPage"));
const ProductsPage = lazy(() => import("./pages/services/ProductsPage"));
const RenewableEnergyPage = lazy(() => import("./pages/services/RenewableEnergyPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const SuccessTeamPage = lazy(() => import("./pages/SuccessTeamPage"));
const TerritoryBenefitPlanPage = lazy(() => import("./pages/TerritoryBenefitPlanPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ConditionsPage = lazy(() => import("./pages/ConditionsPage"));
const PayPage = lazy(() => import("./pages/PayPage"));
const PaySuccessPage = lazy(() => import("./pages/PaySuccessPage"));
const PayCancelledPage = lazy(() => import("./pages/PayCancelledPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const PreviewIndex = lazy(() => import("./pages/preview/PreviewIndex"));
const DesignOne = lazy(() => import("./pages/preview/DesignOne"));
const DesignTwo = lazy(() => import("./pages/preview/DesignTwo"));
const DesignThree = lazy(() => import("./pages/preview/DesignThree"));
const DesignFour = lazy(() => import("./pages/preview/DesignFour"));

const ScrollToTopOnNavigate = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const AppShell = () => {
  const { pathname } = useLocation();
  const isPreview = pathname.startsWith('/preview');

  return (
    <div className="App" itemScope itemType="https://schema.org/Organization">
      <Helmet>
        <html lang="en" />
        <meta name="description" content="GSD - Global Sales & Distribution. Western boutique multi-commodity trading house specializing in supply chain management of energy, raw materials, and base metals. Darwin & Singapore." />
      </Helmet>

      {!isPreview && <WarmGradientOrbs />}
      {!isPreview && <Header />}

      <ScrollToTopOnNavigate />

      <main role="main">
        <div key={pathname} className="animate-fade-up">
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services/technical-advisory" element={<TechnicalAdvisoryPage />} />
              <Route path="/services/sourcing" element={<SourcingPage />} />
              <Route path="/services/products" element={<ProductsPage />} />
              <Route path="/services/renewable-energy" element={<RenewableEnergyPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/team" element={<SuccessTeamPage />} />
              <Route path="/nt-benefits" element={<TerritoryBenefitPlanPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/conditions" element={<ConditionsPage />} />
              <Route path="/pay" element={<PayPage />} />
              <Route path="/pay/success" element={<PaySuccessPage />} />
              <Route path="/pay/cancelled" element={<PayCancelledPage />} />
              <Route path="/preview" element={<PreviewIndex />} />
              <Route path="/preview/1" element={<DesignOne />} />
              <Route path="/preview/2" element={<DesignTwo />} />
              <Route path="/preview/3" element={<DesignThree />} />
              <Route path="/preview/4" element={<DesignFour />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </div>
      </main>

      {!isPreview && <Footer />}
      {!isPreview && <ScrollToTop />}
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
